/**
 * AddExternalContactModal
 * Modal for adding a contact (internal team member or external party) to a
 * project's Kontaktlista. The external tab follows the ShareLinkModal pattern —
 * ExternalUserSelector with a create-new flow; the internal tab picks from the
 * app's internal users.
 */
import React, { useMemo, useState } from 'react';
import { Modal, Button, Form, Alert, Nav } from 'react-bootstrap';
import { Plus } from 'lucide-react';
import ExternalUserSelector from '../../../Components/ExternalUserSelector';
import InternalUserSelector from '../../../Components/InternalUserSelector';
import CreateExternalUserForm from '../../../Components/CreateExternalUserForm';
import { ExternalUser } from '../../../types/guestAccess';
import { Userprofile } from '../../../Helpers/UsersContext';

interface AddExternalContactModalProps {
    show: boolean;
    onHide: () => void;
    onAdd: (userId: string, projectNote: string | undefined, contactType: 'internal' | 'external') => Promise<void>;
    onRefetchUsers: () => Promise<void>;
    submitting: boolean;
    /** UIDs of contacts already linked to this project */
    linkedContactIds: string[];
    /** Internal app users that can be added to the list */
    internalUsers: Userprofile[];
}

type Step = 'select' | 'create-user';
type Tab = 'internal' | 'external';

export const AddExternalContactModal: React.FC<AddExternalContactModalProps> = ({
    show,
    onHide,
    onAdd,
    onRefetchUsers,
    submitting,
    linkedContactIds,
    internalUsers,
}) => {
    const [step, setStep] = useState<Step>('select');
    const [activeTab, setActiveTab] = useState<Tab>('internal');
    const [selectedUser, setSelectedUser] = useState<ExternalUser | null>(null);
    const [selectedInternalId, setSelectedInternalId] = useState('');
    const [projectNote, setProjectNote] = useState('');
    const [error, setError] = useState<string | null>(null);

    // Internal users not already linked to the project.
    const availableInternalUsers = useMemo(
        () => internalUsers.filter((u) => !linkedContactIds.includes(u.key)),
        [internalUsers, linkedContactIds]
    );

    const resetState = () => {
        setStep('select');
        setActiveTab('internal');
        setSelectedUser(null);
        setSelectedInternalId('');
        setProjectNote('');
        setError(null);
    };

    const handleClose = () => {
        resetState();
        onHide();
    };

    const switchTab = (tab: Tab) => {
        setActiveTab(tab);
        setError(null);
        setProjectNote('');
    };

    const handleAddContact = async () => {
        if (activeTab === 'external') {
            // If no user selected, open the create form
            if (!selectedUser) {
                setStep('create-user');
                return;
            }
        } else if (!selectedInternalId) {
            setError('Välj en intern användare');
            return;
        }

        setError(null);

        try {
            if (activeTab === 'external' && selectedUser) {
                await onAdd(selectedUser.uid, projectNote.trim() || undefined, 'external');
            } else {
                await onAdd(selectedInternalId, projectNote.trim() || undefined, 'internal');
            }
            handleClose();
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Kunde inte lägga till kontakt';
            setError(message);
        }
    };

    const handleUserCreated = async (user: ExternalUser) => {
        setSelectedUser(user);
        setStep('select');
        // Refresh the parent's user list so the new user appears in the selector
        await onRefetchUsers();
    };

    const noteField = (
        <Form.Group className="mb-3">
            <Form.Label>Anteckning (valfri)</Form.Label>
            <Form.Control
                as="textarea"
                rows={2}
                value={projectNote}
                onChange={(e) => setProjectNote(e.target.value)}
                placeholder="T.ex. 'Ansvarig för elarbeten'"
            />
            <Form.Text className="text-muted">
                Lägg till en kort notering om personens roll i projektet.
            </Form.Text>
        </Form.Group>
    );

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {step === 'create-user' ? 'Skapa ny extern kontakt' : 'Lägg till kontakt'}
                </Modal.Title>
            </Modal.Header>

            {step === 'create-user' ? (
                <CreateExternalUserForm
                    onUserCreated={handleUserCreated}
                    onCancel={() => setStep('select')}
                />
            ) : (
                <>
                    <Modal.Body>
                        {error && <Alert variant="danger">{error}</Alert>}

                        <Nav variant="tabs" className="mb-3">
                            <Nav.Item>
                                <Nav.Link
                                    active={activeTab === 'internal'}
                                    onClick={() => switchTab('internal')}
                                >
                                    Intern
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link
                                    active={activeTab === 'external'}
                                    onClick={() => switchTab('external')}
                                >
                                    Extern
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>

                        {activeTab === 'internal' ? (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>Välj intern användare</Form.Label>
                                    <InternalUserSelector
                                        users={availableInternalUsers}
                                        value={selectedInternalId}
                                        onChange={setSelectedInternalId}
                                        disabled={submitting}
                                        unassignedLabel="Välj användare..."
                                    />
                                </Form.Group>
                                {selectedInternalId && noteField}
                            </>
                        ) : (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>Välj extern kontakt</Form.Label>
                                    <ExternalUserSelector
                                        onSelect={setSelectedUser}
                                        onCreateNew={() => setStep('create-user')}
                                        selectedUser={selectedUser}
                                        disabledIds={linkedContactIds}
                                    />
                                </Form.Group>
                                {selectedUser && noteField}
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose}>
                            Avbryt
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleAddContact}
                            disabled={submitting}
                        >
                            <Plus size={18} className="me-2" />
                            {submitting
                                ? 'Lägger till...'
                                : activeTab === 'external' && !selectedUser
                                    ? 'Skapa ny kontakt'
                                    : 'Lägg till kontakt'}
                        </Button>
                    </Modal.Footer>
                </>
            )}
        </Modal>
    );
};
